// @flow

import { useCallback, useRef, useMemo, useEffect } from 'react';
import { PromptChatProvider, downloadAsDocx, downloadAsPdf } from '../ai-prompt-management';
import { IconDownloadDocx, IconDownloadPdf } from '../ai-prompt-management/components/DownloadIcons';
import { createThrottledTextDispatcher } from '../ai-prompt-management/utils/throttledDispatch';
import { startReportChatStream } from './services/reportChatStreamService';
import { useFeedbackServiceWithMetadata } from '../ai-prompt-management/context';
import { generateSignature } from '../../utils/report-markdown-transformer';
import { buildReportChatIntroText } from '../../utils/ai-chat-questions';

type ReportRenderContextValue = {
  isReportComplete: boolean,
  reportMarkdown: string | null,
  reportSignature: string | null,
  lastSentSignature: string | null,
  setLastSentSignature: (signature: string | null) => void,
  generateMarkdown: () => string | null,
};

type ReportChatWrapperProps = {
  children: React$Node,
  threadId: string,
  onThreadIdChange?: (id: string) => void,
  onError?: (error: { type: string, message: string }) => void,
  reportRenderContext: ?ReportRenderContextValue,
  staffDetectionDisabled?: boolean,
  demographicsDisabled?: boolean,
  availableMetricGroups?: string[],
};

const ReportChatWrapper = ({
  children,
  threadId,
  onThreadIdChange,
  onError,
  reportRenderContext,
  staffDetectionDisabled = false,
  demographicsDisabled = false,
  availableMetricGroups = [],
}: ReportChatWrapperProps): React$Node => {
  const feedbackService = useFeedbackServiceWithMetadata();
  const threadIdRef = useRef(threadId);

  const introText = useMemo(
    () => buildReportChatIntroText({ staffDetectionDisabled, demographicsDisabled, availableMetricGroups }),
    [staffDetectionDisabled, demographicsDisabled, availableMetricGroups]
  );
  const streamCancelRef = useRef<?Function>(null);
  const isStreamingRef = useRef<boolean>(false);
  const reportContextRef = useRef<?ReportRenderContextValue>(reportRenderContext);
  const throttlerCleanupRef = useRef<?Function>(null);
  
  // Keep refs updated
  if (threadId !== threadIdRef.current) threadIdRef.current = threadId;
  reportContextRef.current = reportRenderContext;

  const streamingAdapter = useCallback((dispatch: Function) => {
    const { dispatch: throttledDispatch, flush, cleanup } = createThrottledTextDispatcher(dispatch);
    throttlerCleanupRef.current = cleanup;
    
    const send = async (prompt: string, agentMessageId: string) => {
      isStreamingRef.current = true;
      let lastEventText = 'Thinking';
      let markdownWasSent = false;
      let signatureToSave: string | null = null;
      
      try {
        if (streamCancelRef.current) {
          streamCancelRef.current();
          streamCancelRef.current = null;
        }

        const ctx = reportContextRef.current;

        // Decide whether to send report markdown with this prompt.
        // Requirements:
        // - Send if it has not been sent in the current thread (lastSentSignature === null)
        // - Send if the report markdown has changed (currentSignature !== lastSentSignature)
        // - Do NOT send if unchanged and already sent
        // NOTE: reportSignature is intentionally invalidated to null while widgets reload.
        // We must be willing to (re)compute markdown/signature on-demand to detect changes.
        let additionalData = undefined;

        if (ctx && ctx.isReportComplete) {
          const lastSent = ctx.lastSentSignature;

          // Compute a current signature if we need it for comparison.
          // Prefer the cached signature if available; otherwise compute from markdown.
          let currentSignature: string | null = ctx.reportSignature;
          let computedMarkdown: string | null = null;

          const needsSignatureForCompare = lastSent !== null;
          if (needsSignatureForCompare && !currentSignature) {
            // Generate markdown on-demand (if not already cached)
            computedMarkdown = ctx.reportMarkdown || ctx.generateMarkdown();
            if (computedMarkdown) {
              currentSignature = generateSignature(computedMarkdown);
            }
          }

          const shouldSendMarkdown =
            lastSent === null ||
            (currentSignature !== null && currentSignature !== lastSent);

          if (shouldSendMarkdown) {
            const markdownForSend = computedMarkdown || ctx.reportMarkdown || ctx.generateMarkdown();
            if (markdownForSend) {
              additionalData = [{
                type: 'report',
                format: 'markdown',
                data: markdownForSend,
              }];
              markdownWasSent = true;
              signatureToSave = generateSignature(markdownForSend);
            }
          }
        }

        const { cancel } = await startReportChatStream({
          threadId: threadIdRef.current || '',
          prompt,
          additionalData,
          onEvent: (status, data) => {
            if (status === 'event') {
              // Lifecycle event (object with phase) vs legacy event (plain string)
              if (typeof data === 'object' && data !== null && data.phase) {
                const { eventId, phase, label, reason } = data;
                if (phase === 'start') {
                  const ev = {
                    id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    eventId: eventId || undefined,
                    text: label || 'Processing',
                    reason: typeof reason === 'string' ? reason : undefined,
                    withDots: true,
                    startAt: Date.now(),
                  };
                  throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                  lastEventText = label || 'Processing';
                } else if (phase === 'stop') {
                  if (eventId) {
                    throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId });
                  } else {
                    console.warn('ReportChatWrapper: tool-lifecycle stop without eventId');
                  }
                } else if (phase === 'error') {
                  if (eventId) {
                    throttledDispatch({ type: 'FINALIZE_EVENT_BY_ID', agentMessageId, eventId, opts: { error: true } });
                  } else {
                    console.warn('ReportChatWrapper: tool-lifecycle error without eventId');
                  }
                }
              } else {
                const eventLabel = typeof data === 'string' ? data : String(data || '');
                if (eventLabel.toLowerCase() === 'thinking' && lastEventText.toLowerCase() === 'thinking') {
                  return;
                }
                const ev = {
                  id: `ev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  text: eventLabel,
                  withDots: true,
                  startAt: Date.now(),
                };
                throttledDispatch({ type: 'APPEND_EVENT', agentMessageId, event: ev });
                lastEventText = eventLabel;
              }
            } else if (status === 'data-display') {
              // Data display definition from dataDisplayTool
              if (data && data.displayId) {
                const { displayId, ...definition } = data;
                throttledDispatch({
                  type: 'APPEND_DATA_DISPLAY',
                  agentMessageId,
                  displayId,
                  definition,
                });
              }
            } else if (status === 'trace-id') {
              const traceId = data && data.traceId;
              if (traceId) {
                throttledDispatch({ type: 'SET_TRACE_ID', agentMessageId, traceId, sessionId: threadIdRef.current });
              }
            } else if (status === 'agent-message') {
              const msgData = data && typeof data === 'object' ? data.message : data;
              const tokenStr = typeof msgData === 'string' ? msgData : String(msgData || '');
              throttledDispatch({ type: 'APPEND_AGENT_TEXT', agentMessageId, textChunk: tokenStr });
            }
          },
          onError: (message) => {
            flush();
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId, opts: { error: true } });
            throttledDispatch({ type: 'SET_ERROR', error: { type: 'generic', message: String(message || 'An error occurred') } });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
          },
          onClose: () => {
            flush();
            throttledDispatch({ type: 'FINALIZE_LAST_EVENT', agentMessageId });
            throttledDispatch({ type: 'END_STREAM' });
            isStreamingRef.current = false;
            
            // Update lastSentSignature if markdown was sent
            if (markdownWasSent && signatureToSave && reportContextRef.current) {
              reportContextRef.current.setLastSentSignature(signatureToSave);
            }
          },
        });

        streamCancelRef.current = cancel;
      } catch (err) {
        dispatch({ type: 'SET_ERROR', error: { type: 'generic', message: err.message || 'Error starting stream' } });
        dispatch({ type: 'END_STREAM' });
        isStreamingRef.current = false;
      }
    };

    const cancel = () => {
      if (streamCancelRef.current) {
        streamCancelRef.current();
        streamCancelRef.current = null;
      }
      isStreamingRef.current = false;
    };

    const reset = () => {
      cancel();
    };

    return { send, cancel, reset, get isStreaming() { return isStreamingRef.current; } };
  }, []);

  useEffect(() => () => {
    if (streamCancelRef.current) {
      streamCancelRef.current();
      streamCancelRef.current = null;
    }
    if (throttlerCleanupRef.current) {
      throttlerCleanupRef.current();
      throttlerCleanupRef.current = null;
    }
  }, []);

  // Define download actions for Report Chat
  const downloadActions = useMemo(() => [
    {
      id: 'docx',
      icon: <IconDownloadDocx width={16} height={16} />,
      tooltip: 'Download this message as DOCX',
      handler: downloadAsDocx,
    },
    {
      id: 'pdf',
      icon: <IconDownloadPdf width={16} height={16} />,
      tooltip: 'Download this message as PDF',
      handler: downloadAsPdf,
    },
  ], []);

  // Disable prompt buttons while report is loading
  const promptButtonsDisabled = !reportRenderContext?.isReportComplete;

  return (
    <PromptChatProvider
      sessionId={threadId}
      initialAgentText={introText}
      suppressIntroMessage={false}
      streamingAdapter={streamingAdapter}
      feedbackService={feedbackService}
      onError={onError}
      onSessionIdChange={onThreadIdChange}
      downloadActions={downloadActions}
      promptButtonsDisabled={promptButtonsDisabled}
    >
      {children}
    </PromptChatProvider>
  );
};

export default ReportChatWrapper;
